import { useEffect, useState, useCallback } from "react";
import moment from "moment";
import Card from '../../../../componentes/contenedores/Card';
import Page from '../../../../componentes/plantilla/Page';
import GraficoBarra from '../../../../componentes/graficos/Barras';
import { AxiosPublico } from "../../../../componentes/axios/Axios";
import {
    VentaAnalisis,
    VentaTopClientes,
    VentaTopFrecuenciaClientes,
    VentaTopProductos,
    VentaTopProductosMenosVendidos,
    VentaRentabilidadProductos,
    InventariosExistencia,
    InventariosRotacion
} from '../../../../configuracion/apiUrls';
import useFechaFiltro from "../../../../componentes/metodos/LogicaFiltro";
import FiltroFechaUI from "../../../../componentes/metodos/FiltroFechaUI";
moment.locale("es");

const HomeAnalisisVentas = () => {
    const [cargando, setCargando] = useState(true);
    const [chartVentas, setChartVentas] = useState(null);
    const [chartTopClientes, setChartTopClientes] = useState(null);
    const [chartRankingProductos, setChartRankingProductos] = useState(null);
    const [chartFrecuenciaClientes, setChartFrecuenciaClientes] = useState(null);
    const [chartMenosVendidos, setChartMenosVendidos] = useState(null);
    const [chartRentabilidad, setChartRentabilidad] = useState(null);
    const [chartExistencias, setChartExistencias] = useState(null);
    const [chartRotacion, setChartRotacion] = useState(null);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resVentas, resClientes, resProds, resFrecuencia, resMenos, resRentabilidad, resExistencia, resRotacion] = await Promise.all([
                AxiosPublico.get(VentaAnalisis, { params }),
                AxiosPublico.get(VentaTopClientes, { params }),
                AxiosPublico.get(VentaTopProductos, { params }),
                AxiosPublico.get(VentaTopFrecuenciaClientes, { params }),
                AxiosPublico.get(VentaTopProductosMenosVendidos, { params }),
                AxiosPublico.get(VentaRentabilidadProductos, { params }),
                AxiosPublico.get(InventariosExistencia, { params }),
                AxiosPublico.get(InventariosRotacion, { params }),
            ]);

            // 1. Tendencia de Ventas (Monetario)
            if (resVentas.data?.length > 0) {
                setChartVentas({
                    labels: resVentas.data.map(item => new Date(item.periodo).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit' })),
                    data: resVentas.data.map(item => parseFloat(item.totalVentas)),
                    colors: resVentas.data.map((_, i) => `hsla(${60 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Ventas Totales por Día"
                });
            } else { setChartVentas(null); }

            // 2. Gasto por Cliente (Monetario)
            if (resClientes.data?.length > 0) {
                setChartTopClientes({
                    labels: resClientes.data.map(c => c.Cliente?.nombreCorto || "S/N"),
                    data: resClientes.data.map(c => parseFloat(c.totalGastado)),
                    colors: resClientes.data.map((_, i) => `hsla(${120 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 clientes con mayor valor de compras"
                });
            } else { setChartTopClientes(null); }

            // 3. Frecuencia de Compra (Cantidad)
            if (resFrecuencia.data?.length > 0) {
                setChartFrecuenciaClientes({
                    labels: resFrecuencia.data.map(c => c.Cliente?.nombreCorto || "S/N"),
                    data: resFrecuencia.data.map(c => parseInt(c.frecuenciaCompra)),
                    colors: resFrecuencia.data.map((_, i) => `hsla(${120 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 clientes con mayor frecuencia de compra"
                });
            } else { setChartFrecuenciaClientes(null); }

            // 4. Productos más Vendidos (Cantidad)
            if (resProds.data?.length > 0) {
                setChartRankingProductos({
                    labels: resProds.data.map(p => p.producto?.nombre || "N/A"),
                    data: resProds.data.map(p => parseFloat(p.cantidadTotalVendida)),
                    colors: resProds.data.map((_, i) => `hsla(${180 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 productos más vendidos (por cantidad)"
                });
            } else { setChartRankingProductos(null); }

            // 5. Utilidad/Rentabilidad (Monetario)
            if (resRentabilidad.data?.length > 0) {
                setChartRentabilidad({
                    labels: resRentabilidad.data.map(p => p.producto?.nombre || "N/A"),
                    data: resRentabilidad.data.map(p => parseFloat(p.utilidadTotalGenerada)),
                    colors: resRentabilidad.data.map((_, i) => `hsla(${180 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 productos más rentables (por utilidad)"
                });
            } else { setChartRentabilidad(null); }

            // 6. Productos Menos Vendidos (Cantidad)
            if (resMenos.data?.length > 0) {
                setChartMenosVendidos({
                    labels: resMenos.data.map(p => p.producto?.nombre || "N/A"),
                    data: resMenos.data.map(p => parseFloat(p.cantidadTotalVendida)),
                    colors: resMenos.data.map((_, i) => `hsla(${180 + (i * 20)}, 70%, 50%, 0.8)`),
                    titulo: "Top 10 productos con menor número de ventas (por cantidad)"
                });
            } else { setChartMenosVendidos(null); }
            // 7. Existencias en Inventario (Cantidad)
            if (resExistencia.data?.length > 0) {
                setChartExistencias({
                    labels: resExistencia.data.map(p => p.nombre || "N/A"),
                    data: resExistencia.data.map(p => parseFloat(p.existenciaTotal)),
                    colors: resExistencia.data.map(() => `rgba(153, 102, 255, 0.8)`),
                    titulo: "Stock Actual por Producto"
                });
            } else { setChartExistencias(null); }

            // 8. Rotación de Productos (Cantidad por día)
            if (resRotacion.data?.length > 0) {
                setChartRotacion({
                    labels: resRotacion.data.map(item => new Date(item.periodo).toLocaleDateString()),
                    data: resRotacion.data.map(item => parseFloat(item.cantidadVendida)),
                    colors: resRotacion.data.map(() => `rgba(255, 159, 64, 0.8)`),
                    titulo: "Rotación Diaria (Salida de Mercancía)"
                });
            } else { setChartRotacion(null); }

        } catch (error) {
            console.error("Error al cargar métricas:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    return (
        <Page datos={{ titulo: { titulo: "Análisis de Ventas", url: "#", nombreUrl: "Analisis" } }}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />
                {cargando ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2">Calculando estadísticas...</p>
                    </div>
                ) : (
                    <div className="row g-4">
                        <div className="col-12">
                            {chartVentas && <GraficoBarra datos={chartVentas} esMonetario={true} />}
                        </div>
                        <div className="col-md-6">
                            {chartTopClientes && <GraficoBarra datos={chartTopClientes} esMonetario={true} />}
                        </div>
                        <div className="col-md-6">
                            {chartFrecuenciaClientes && <GraficoBarra datos={chartFrecuenciaClientes} esMonetario={false} />}
                        </div>
                        <div className="col-md-6">
                            {chartRankingProductos && <GraficoBarra datos={chartRankingProductos} esMonetario={false} />}
                        </div>
                        <div className="col-md-6">
                            {chartRentabilidad && <GraficoBarra datos={chartRentabilidad} esMonetario={true} />}
                        </div>
                        <div className="col-12">
                            {chartMenosVendidos && <GraficoBarra datos={chartMenosVendidos} esMonetario={false} />}
                        </div>
                        <div className="col-md-6">
                            {chartExistencias && <GraficoBarra datos={chartExistencias} esMonetario={false} />}
                        </div>
                        <div className="col-md-6">
                            {chartRotacion && <GraficoBarra datos={chartRotacion} esMonetario={false} />}
                        </div>
                    </div>
                )}
            </Card>
        </Page>
    );
};

export default HomeAnalisisVentas;